<template>
	<div class="w-full pb-8">
		<div
			v-if="isCartEmpty"
			class="text-secondary-600 p-4 text-xs font-semibold"
		>
			{{ t('document.noItemsInCart') }}
		</div>
		<div
			v-else
			class="divide-y divide-slate-200 text-xs *:py-4"
		>
			<!-- Package Items -->
			<div
				v-for="cartItem in groupedCartItems.packages"
				:key="cartItem.lineItemId"
				class="flex flex-col pb-0!"
			>
				<div class="cart-item-row border-b border-slate-200! py-4">
					<InlineClipboardCopy
						:displayText="cartItem.lineItem?.name || ''"
						textClasses="copy-label"
					/>
					<InlineClipboardCopy
						:displayText="
							formatPrice(getCartItemPrice(selectedPlan!, cartItem).price)
						"
						:copyRawValue="getCartItemPrice(selectedPlan!, cartItem).price"
						textClasses="copy-price"
					/>
				</div>

				<div
					v-for="subItem in getFilteredSubItems(cartItem)"
					:key="subItem.lineItemId"
					class="cart-sub-item-row"
				>
					<InlineClipboardCopy
						:displayText="subItem.lineItem?.name || ''"
						textClasses="copy-label"
					/>
					<InlineClipboardCopy
						:displayText="
							formatPrice(getCartItemPrice(selectedPlan!, subItem).price)
						"
						:copyRawValue="getCartItemPrice(selectedPlan!, subItem).price"
						textClasses="copy-price"
					/>
				</div>
			</div>

			<!-- Non-Package Items -->
			<div
				v-for="cartItem in groupedCartItems.nonPackages"
				:key="cartItem.lineItemId"
				class="cart-item-row"
			>
				<InlineClipboardCopy
					:displayText="cartItem.lineItem?.name || ''"
					textClasses="copy-label"
				/>
				<InlineClipboardCopy
					:displayText="
						formatPrice(getCartItemPrice(selectedPlan!, cartItem).price)
					"
					:copyRawValue="getCartItemPrice(selectedPlan!, cartItem).price"
					textClasses="copy-price"
				/>
			</div>

			<!-- Custom Items -->
			<div
				v-for="cartItem in groupedCartItems.custom"
				:key="cartItem.id"
				class="cart-item-row"
			>
				<InlineClipboardCopy
					:displayText="cartItem.title"
					textClasses="copy-label"
				/>
				<InlineClipboardCopy
					:displayText="formatPrice(cartItem.price)"
					:copyRawValue="cartItem.price"
					textClasses="copy-price"
				/>
			</div>

			<div
				v-if="hasBirthDate"
				class="p-0!"
			>
				<div
					v-if="selectedPlan?.insurance?.downPayment !== undefined"
					class="cart-item-row"
				>
					<InlineClipboardCopy
						:displayText="t('document.downPayment')"
						textClasses="copy-label"
					/>
					<InlineClipboardCopy
						:displayText="formatPrice(selectedPlan.insurance.downPayment)"
						:copyRawValue="selectedPlan.insurance.downPayment"
						textClasses="copy-price"
					/>
				</div>

				<div
					v-if="!selectedPlan?.insurance?.downPayment"
					class="cart-item-row"
				>
					<InlineClipboardCopy
						:displayText="t('document.firstPayment')"
						textClasses="copy-label"
					/>
					<InlineClipboardCopy
						:displayText="formatPrice(paymentOptions?.firstPaymentAmount.value)"
						:copyRawValue="paymentOptions?.firstPaymentAmount.value"
						textClasses="copy-price"
					/>
				</div>

				<div class="cart-item-row">
					<InlineClipboardCopy
						:displayText="t('oap.oap')"
						textClasses="copy-label"
					/>
					<InlineClipboardCopy
						:displayText="formatPrice(paymentOptions?.oapPrice.value)"
						:copyRawValue="paymentOptions?.oapPrice.value"
						textClasses="copy-price"
					/>
				</div>

				<div class="cart-item-row">
					<InlineClipboardCopy
						:displayText="t('payment.totalFuneralAmount')"
						textClasses="copy-label"
					/>
					<InlineClipboardCopy
						:displayText="formatPrice(paymentOptions?.totalFuneralAmount.value)"
						:copyRawValue="paymentOptions?.totalFuneralAmount.value"
						textClasses="copy-price"
					/>
				</div>

				<div
					v-for="(option, index) in paymentOptions?.paymentSummaryOptions.value"
					:key="index"
					class="cart-item-row font-bold last:border-b-0"
				>
					<InlineClipboardCopy :displayText="option.label" />
					<InlineClipboardCopy
						:displayText="formatPrice(option.amount)"
						:copyRawValue="option.amount"
					/>
				</div>
			</div>
			<div
				v-else
				class="text-secondary-600! p-4 text-xs font-semibold"
			>
				{{ t('document.noBirthDate') }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import InlineClipboardCopy from './InlineClipboardCopy.vue'
import { PbProductType } from '@/enums/PbProductType'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type {
	CartItemAndLineItem,
	CartSubItemAndLineItem,
} from '@/mixins/merchandise'
import { usePaymentOptions } from '@/mixins/paymentOptions'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { computed } from 'vue'

const props = defineProps<{ selectedPlanId: string }>()

const { t } = usePresentationLocale()
const sessionStore = useSessionStore()
const planStore = usePlanStore()
const { getPlanPaymentOptions } = usePaymentOptions()
const { getPlanCartItems, getCartItemPrice, formatPrice } = useMerchandise()

// Selected plan based on the selected plan id
const selectedPlan = computed(() => planStore.getPlan(props.selectedPlanId))

// Payment options for the selected plan
const paymentOptions = computed(() =>
	selectedPlan.value ? getPlanPaymentOptions(selectedPlan.value) : null
)

// Check if the plan has a birth date
const hasBirthDate = computed(() => !!selectedPlan.value?.birthDate)

// Group cart items by category
const groupedCartItems = computed(() => {
	if (!selectedPlan.value) return { packages: [], nonPackages: [], custom: [] }

	const allCartItems = getPlanCartItems(selectedPlan.value.id) ?? []

	return {
		packages: allCartItems.filter(
			(item) =>
				item.lineItem?.category === PbProductType.Package && !item.isSoftDeleted
		),
		nonPackages: allCartItems.filter(
			(item) =>
				item.lineItem?.category !== PbProductType.Package && !item.isSoftDeleted
		),
		custom: selectedPlan.value.customCartItems.filter(
			(item) => !item.isSoftDeleted
		),
	}
})

// Get the subitems for a cart item
const getFilteredSubItems = (cartItem: CartItemAndLineItem) => {
	const allPackageProducts =
		sessionStore.activePriceBook?.getProductsInPackage(cartItem.lineItemId) ??
		[]
	const sortedPackageProducts =
		sessionStore.activePriceBook?.getSortedPackageProducts(
			allPackageProducts,
			true
		) ?? []
	return sortedPackageProducts
		.map((pkgProduct) =>
			(cartItem.subItems ?? []).find(
				(item) => item.lineItemId === pkgProduct.refSpbLineProduct
			)
		)
		.filter((item): item is CartSubItemAndLineItem => !!item)
}

// Check if the cart is empty
const isCartEmpty = computed(
	() =>
		groupedCartItems.value.packages.length === 0 &&
		groupedCartItems.value.nonPackages.length === 0 &&
		groupedCartItems.value.custom.length === 0
)
</script>

<style scoped>
@reference "tailwindcss";
.cart-item-row {
	@apply flex justify-between gap-1 border-b border-slate-200 py-4 *:px-2 hover:bg-[#F5F9FF];
}

.cart-sub-item-row {
	@apply flex justify-between border-b border-slate-200 py-3 pl-2 *:px-2 last:border-0 hover:bg-[#F5F9FF];
}

:deep(.copy-label) {
	@apply font-bold text-[#0C58B7];
}
</style>
